// Client-side version of `app/lib/action_text_sanitizer.rb`.
//
// Runs on `trix-before-paste` so the pasted content does not reach the
// editor as an attachment that the backend would remove on save. Without
// this, the user sees an image in the editor, clicks Save, and the image
// disappears with no explanation.
//
// The backend is still the main check: this filter can be skipped
// (DevTools), so the server runs `ActionTextSanitizer` on save anyway.

// Same allowlist as `render_safe_url?` in the Ruby sanitizer.
const RENDER_SAFE_URL = /^(?:https?|data):/i;

const isRenderSafeUrl = (url) => {
  if (!url) return true; // empty src does not cause problems on render
  return RENDER_SAFE_URL.test(url) || url.startsWith('/');
};

const parsePayload = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const isInsidePreservedAttachment = (img) => {
  const figure = img.closest('figure[data-trix-attachment]');
  if (!figure) return false;
  const payload = parsePayload(figure.getAttribute('data-trix-attachment'));
  return Boolean(payload?.sgid);
};

// Returns `{ html, removedAny }` - the cleaned HTML and a flag the caller
// can use to show a message to the user when something was actually removed.
//
// `allowExternalImages: false` removes every image, including external
// http(s) ones. Used in OIM activity descriptions, where the only allowed
// way to add an image is the Trix upload button.
export const sanitizePastedHtml = (
  html,
  { allowExternalImages = true } = {},
) => {
  if (!html) return { html, removedAny: false };

  const doc = new DOMParser().parseFromString(html, 'text/html');
  let removedAny = false;

  // Shape of the paste in Trix: <figure data-trix-attachment="...JSON...">
  // or <a data-trix-attachment="..."> etc. We remove it when there is no
  // `sgid`, meaning it was not uploaded through our own direct_uploads endpoint.
  doc.querySelectorAll('[data-trix-attachment]').forEach((node) => {
    const payload = parsePayload(node.getAttribute('data-trix-attachment'));
    if (!payload) {
      node.remove();
      removedAny = true;
      return;
    }
    if (payload.sgid) return;
    if (!allowExternalImages || !isRenderSafeUrl(payload.url)) {
      node.remove();
      removedAny = true;
    }
  });

  // Already-converted shape: less common in clipboard payloads, but technically possible.
  doc.querySelectorAll('action-text-attachment').forEach((node) => {
    if (node.getAttribute('sgid')) return;
    if (!allowExternalImages || !isRenderSafeUrl(node.getAttribute('url'))) {
      node.remove();
      removedAny = true;
    }
  });

  // Plain <img> tags. When external images are allowed we keep render-safe
  // ones (admin can paste CDN images in Articles, etc.). Otherwise we remove
  // all of them - except the preview <img> inside a preserved Trix-uploaded
  // figure, which must stay so the attachment structure remains valid.
  doc.querySelectorAll('img').forEach((node) => {
    if (isInsidePreservedAttachment(node)) return;
    if (!allowExternalImages || !isRenderSafeUrl(node.getAttribute('src'))) {
      node.remove();
      removedAny = true;
    }
  });

  return { html: doc.body.innerHTML, removedAny };
};
